import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { getAdminSession } from '../utils/api';
import './MainLayout.css';

const normalizeBaseUrl = (value, fallback) => {
  if (typeof value === 'string' && value.trim()) {
    return value.replace(/\/$/, '');
  }
  return fallback;
};

const resolvePublicBaseUrl = () => {
  const envUrl = normalizeBaseUrl(process.env.REACT_APP_PUBLIC_SITE_URL, '');
  if (envUrl) return envUrl;
  if (typeof window === 'undefined') return 'https://servisml.sk';
  const host = window.location.hostname || '';
  if (host.startsWith('admin.') || host.startsWith('rezervacia.')) {
    return `https://${host.replace(/^(admin|rezervacia)\./, '')}`;
  }
  return 'https://servisml.sk';
};

const readCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  if (!match) return '';
  return match.split('=')[1] || '';
};

const decodeValue = (value) => {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch (err) {
    return value;
  }
};

const getInitialAdminStatus = () => {
  if (typeof document === 'undefined') {
    return { loading: true, loggedIn: false, firstName: '' };
  }
  const logged = readCookie('admin_logged_in') === '1';
  const cookieFirstName = decodeValue(readCookie('admin_first_name'));
  const name = cookieFirstName || decodeValue(readCookie('admin_display_name'));
  const firstName = name.trim().split(/\s+/)[0] || '';
  return { loading: false, loggedIn: logged, firstName };
};

const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
};

const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const publicBaseUrl = resolvePublicBaseUrl();
  const adminBaseUrl = normalizeBaseUrl(process.env.REACT_APP_ADMIN_URL, publicBaseUrl);
  const publicAsset = (path) => `${publicBaseUrl}${path}`;
  const [adminStatus, setAdminStatus] = useState(getInitialAdminStatus);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      try {
        const response = await getAdminSession();
        const data = response.data?.data;
        if (!active) return;
        if (data?.logged_in && data.user) {
          const fullName = decodeValue(data.user.cele_meno || data.user.prihlasovacie_meno || '');
          const firstName = fullName.trim().split(/\s+/)[0] || '';
          setAdminStatus({ loading: false, loggedIn: true, firstName });
          return;
        }
        setAdminStatus({ loading: false, loggedIn: false, firstName: '' });
      } catch (err) {
        if (!active) return;
        setAdminStatus({ loading: false, loggedIn: false, firstName: '' });
      }
    };

    const handleSessionUpdate = () => {
      loadSession();
    };

    const handleFocus = () => {
      const logged = readCookie('admin_logged_in') === '1';
      const cookieFirstName = decodeValue(readCookie('admin_first_name'));
      const name = cookieFirstName || decodeValue(readCookie('admin_display_name'));
      const firstName = name.trim().split(/\s+/)[0] || '';
      setAdminStatus({ loading: false, loggedIn: logged, firstName });
    };

    loadSession();

    window.addEventListener('admin-session-updated', handleSessionUpdate);
    window.addEventListener('admin-session-cleared', handleSessionUpdate);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      active = false;
      window.removeEventListener('admin-session-updated', handleSessionUpdate);
      window.removeEventListener('admin-session-cleared', handleSessionUpdate);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, []);

  const adminHref = adminStatus.loggedIn
    ? `${adminBaseUrl}/admin/dashboard`
    : `${adminBaseUrl}/admin/login`;
  const adminLabel = adminStatus.loggedIn
    ? `Administrácia${adminStatus.firstName ? ` – ${adminStatus.firstName}` : ''}`
    : 'Prihlásiť sa';

  const openMobileAppWithFallback = (event, appUrl, webUrl) => {
    if (typeof window === 'undefined' || !isMobileDevice()) {
      return;
    }

    event.preventDefault();

    const startTime = Date.now();
    const fallbackTimer = window.setTimeout(() => {
      if (Date.now() - startTime < 1700) {
        window.location.href = webUrl;
      }
    }, 900);

    const clearFallback = () => window.clearTimeout(fallbackTimer);
    window.addEventListener('pagehide', clearFallback, { once: true });
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) clearFallback();
      },
      { once: true }
    );

    window.location.href = appUrl;
  };

  return (
    <div className="main-layout">
      {/* Sidebar Menu (Mobile) */}
      <aside className={`main-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <a href={publicBaseUrl} className="sidebar-logo" onClick={closeSidebar}>
            <img
              src={publicAsset('/assets/images/logos/Logo nazov.webp')}
              alt="ML Servis"
              className="sidebar-logo-img"
            />
          </a>
        </div>

        <nav className="sidebar-nav">
          <a href={publicBaseUrl} className="sidebar-link" onClick={closeSidebar}>
            Domov
          </a>
          <a href={`${publicBaseUrl}/o-nas/`} className="sidebar-link" onClick={closeSidebar}>
            O nás
          </a>
          <a href={`${publicBaseUrl}/kontakt/`} className="sidebar-link" onClick={closeSidebar}>
            Kontakt
          </a>
          <a href={`${publicBaseUrl}/faq/`} className="sidebar-link" onClick={closeSidebar}>
            FAQ
          </a>
          <a href={`${publicBaseUrl}/certifikaty/`} className="sidebar-link" onClick={closeSidebar}>
            Certifikáty
          </a>
          <a href={`${publicBaseUrl}/nase-sluzby/`} className="sidebar-link" onClick={closeSidebar}>
            Naše služby
          </a>
          <a href={`${publicBaseUrl}/galeria/`} className="sidebar-link" onClick={closeSidebar}>
            Galéria
          </a>
          <Link to="/moja-rezervacia" className="sidebar-link" onClick={closeSidebar}>
            Moja rezervácia
          </Link>
        </nav>

        <div className="sidebar-footer">
          <Link to="/rezervacia" className="btn btn-primary btn-block" onClick={closeSidebar}>
            Rezervovať termín
          </Link>
          <Link to={adminHref} className="btn btn-secondary btn-block" onClick={closeSidebar}>
            {adminLabel}
          </Link>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}

      {/* Main Content Area */}
      <div className="main-wrapper">
        {/* Header */}
        <header className="main-header">
          <div className="header-content">
            {/* Hamburger Button */}
            <button 
              className={`hamburger ${sidebarOpen ? 'open' : ''}`}
              onClick={toggleSidebar}
              aria-label="Menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            {/* Logo */}
            <a href={publicBaseUrl} className="header-logo">
              <img
                src={publicAsset('/assets/images/logos/Logo nazov.webp')}
                alt="ML Servis"
                className="logo-image"
              />
            </a>

            {/* Desktop Navigation */}
            <nav className="desktop-nav">
              <a href={publicBaseUrl} className="nav-link">Domov</a>
              <a href={`${publicBaseUrl}/o-nas/`} className="nav-link">O nás</a>
              <a href={`${publicBaseUrl}/kontakt/`} className="nav-link">Kontakt</a>
              <a href={`${publicBaseUrl}/faq/`} className="nav-link">FAQ</a>
              <a href={`${publicBaseUrl}/certifikaty/`} className="nav-link">Certifikáty</a>
              <a href={`${publicBaseUrl}/nase-sluzby/`} className="nav-link">Naše služby</a>
              <a href={`${publicBaseUrl}/galeria/`} className="nav-link">Galéria</a>
              <Link to="/moja-rezervacia" className={`nav-link ${location.pathname === '/moja-rezervacia' ? 'active' : ''}`}>Moja rezervácia</Link>
            </nav>

            {/* Header Actions */}
            <div className="header-actions">
              <Link to="/rezervacia" className="btn btn-primary">
                Rezervovať termín
              </Link>
              <Link to={adminHref} className="admin-link">
                {adminLabel}
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="main-content">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="main-footer">
          <div className="container">
            <div className="footer-top">
              <div className="footer-logo-side">
                <img
                  src={publicAsset('/assets/images/logos/Logo footer.webp')}
                  alt="ML Servis"
                  className="footer-logo-large"
                />
              </div>

              <div className="footer-left">
                <div className="footer-brand-text">
                  <h3>Servis a montáž vykurovacej techniky</h3>
                  <p>
                    Profesionálny servis a oprava vykurovacích systémov. Rýchle termíny, férové ceny a čistá práca.
                  </p>
                </div>

                <div className="footer-links">
                  <div className="footer-col">
                    <h4>Spoločnosť</h4>
                    <a href={`${publicBaseUrl}/o-nas/`}>O nás</a>
                    <a href={`${publicBaseUrl}/certifikaty/`}>Certifikáty</a>
                  </div>
                  <div className="footer-col">
                    <h4>Informácie</h4>
                    <a href={`${publicBaseUrl}/faq/`}>FAQ</a>
                    <a href={`${publicBaseUrl}/kontakt/`}>Kontakt</a>
                  </div>
                  <div className="footer-col">
                    <h4>Služby</h4>
                    <a href={`${publicBaseUrl}/nase-sluzby/`}>Naše služby</a>
                    <Link to="/moja-rezervacia">Moja rezervácia</Link>
                  </div>
                  <div className="footer-col footer-col-connect">
                    <h4>Sledujte nás</h4>
                    <div className="footer-connect-links">
                      <a
                        href="https://www.instagram.com/mlservis_kurenie_chladenie/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        onClick={(event) =>
                          openMobileAppWithFallback(
                            event,
                            'instagram://user?username=mlservis_kurenie_chladenie',
                            'https://www.instagram.com/mlservis_kurenie_chladenie/'
                          )
                        }
                      >
                        <img src={publicAsset('/assets/images/logos/instagram.svg')} alt="Instagram" />
                      </a>
                      <a
                        href="https://www.facebook.com/share/17vnK3RjWT/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        onClick={(event) =>
                          openMobileAppWithFallback(
                            event,
                            'fb://facewebmodal/f?href=https://www.facebook.com/share/17vnK3RjWT/',
                            'https://www.facebook.com/share/17vnK3RjWT/'
                          )
                        }
                      >
                        <img src={publicAsset('/assets/images/logos/facebook.svg')} alt="Facebook" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="footer-brands">
              <div className="footer-brands-title">Servisujeme značky</div>
              <div className="brand-list">
                <span className="brand-pill">Protherm</span>
                <span className="brand-pill">Vaillant</span>
                <span className="brand-pill">Attack</span>
                <span className='brand-pill'>Mitsubishi</span>
                <span className="brand-pill">Atag</span>
                <span className='brand-pill'>Ferroli</span>
                <span className='brand-pill'>Regulus</span>
                <span className='brand-pill'>Atmos</span>
                <span className='brand-pill'>Aux</span>
                <span className='brand-pill'>Naitec</span>
              </div>
            </div>

            <div className="footer-bottom">
              <p>© 2026 ML Servis. Všetky práva vyhradené. Vytvoril FK.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MainLayout;
